import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useState } from 'react';

import { TableColumnsType } from 'antd';

import { GET_IT_APPLICATION_WITH_WORKAROUNDS } from './queries';
import {
  ItApplication,
  ITApplicationWithWorkarounds,
  Workaround,
  WorkaroundDefinedType,
} from './types';

import { TierBadge } from '../../../dataInputs/tiers/components';
import { tableSortingMethods } from '../../../../utils';

import TableV3 from '../../../../components/generic/Table/TableV3';
import QueryErrorBoundary from '../../../../components/error-handling';
import TitleUpdater from '../../../../components/common/TitleUpdater';

import { Footer, Loading, TitleBar } from '../../../../components';
import { useLocationName } from '../../../../hooks';
import { renderComplianceBadges } from '../../utils/complianceBadgeRenderer';
import { ComplianceStatusEnum } from '../../../../shared/types/business-functions-workarounds';
import { CollapsibleText } from '../../../../components/generic/TextTruncation';

const ApplicationWorkaround = () => {
  const { applicationUUID, locationUUID } = useParams();
  const { locationName, isLocationNameLoading, locationNameError } =
    useLocationName(locationUUID);

  const [itApplication, setItApplication] = useState<ItApplication | null>(
    null,
  );

  const { error, loading } = useQuery<ITApplicationWithWorkarounds>(
    GET_IT_APPLICATION_WITH_WORKAROUNDS,
    {
      variables: { uuid: applicationUUID, locationUUID },
      onCompleted: ({ getITApplicationWithWorkarounds: itApplication }) => {
        setItApplication(itApplication);
      },
    },
  );

  const createWorkaroundColumns = (
    titleColumnName: string,
  ): TableColumnsType<Workaround> => [
    {
      title: titleColumnName,
      width: 200,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (name: string) => (
        <span className="custom-table--column ml-10">{name}</span>
      ),
    },
    {
      title: 'Runtime with workaround',
      width: 200,
      dataIndex: 'runtimeHours',
      key: 'runtimeHours',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(a.runtimeHours, b.runtimeHours),
      render: (runtimeHours: number | null) => {
        let runtime: string = `${runtimeHours} hours`;
        if (!runtimeHours) {
          runtime = WorkaroundDefinedType.NO_WORKAROUND;
        } else if (runtimeHours === Number.MAX_VALUE) {
          runtime = 'N/A';
        }
        return runtime === WorkaroundDefinedType.NO_WORKAROUND ? (
          <span className="text-[#BABABA]">{runtime}</span>
        ) : (
          <span className="custom-table--column">{runtime}</span>
        );
      },
    },
    {
      title: (
        <span>
          Workaround <br />
          Timeline Compliance
        </span>
      ),
      width: 200,
      dataIndex: 'workaroundTimelineCompliance',
      key: 'workaroundTimelineCompliance',
      align: 'center',
      render: (complianceData: Record<string, ComplianceStatusEnum>) => {
        return renderComplianceBadges(complianceData, {
          isWorkaroundNeeded: true,
        });
      },
    },
    {
      title: 'Workaround Description',
      dataIndex: 'workaroundDescription',
      key: 'workaroundDescription',
      sorter: (a, b) =>
        tableSortingMethods.string(
          a.workaroundDescription,
          b.workaroundDescription,
        ),
      width: 200,
      align: 'left',
      render: (description: string) => {
        return (
          <div className="text-sm font-normal text-black">
            <CollapsibleText text={description} />
          </div>
        );
      },
    },
  ];

  // Generate column definitions using the reusable function
  const businessFunctionsColumns =
    createWorkaroundColumns('Business Functions');
  const qualitativeFactorsColumns = createWorkaroundColumns(
    'Qualitative Factors',
  );
  const processAreasColumns = createWorkaroundColumns('Process Areas');

  const title = `${
    locationUUID ? 'Location' : 'Enterprise'
  } Application Workarounds`;
  const subtitle = `${itApplication?.name} at ${
    locationUUID ? locationName : `Enterprise Level`
  }`;

  let headerContent;

  if (isLocationNameLoading) {
    headerContent = <Loading width="55px" height="55px" />;
  } else {
    headerContent = <TitleBar title={title} subtitle={subtitle} />;
  }

  const backPath = `/cyber/${
    locationUUID ? `location-level/${locationUUID}` : `enterprise-level`
  }/application-workarounds`;

  return (
    <>
      <TitleUpdater title={title} />
      <QueryErrorBoundary error={locationNameError}>
        {headerContent}
      </QueryErrorBoundary>
      <QueryErrorBoundary error={error}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col">
            <div className="w-full text-base p-4 font-semibold bg-[#F8F8F8] border border-[#CDCDCD] rounded-t-lg">
              Overview
            </div>
            <div className="w-full text-base p-4 font-semibold border-b border-x border-[#CDCDCD]">
              <span className="font-normal">Name:</span> {itApplication?.name}
            </div>
            <div className="w-full text-base p-4 font-semibold border-b border-x border-[#CDCDCD]">
              <span className="font-normal">Description:</span>{' '}
              {itApplication?.description}
            </div>
            <div className="w-full text-base p-4 border-b border-x border-[#CDCDCD] rounded-b-lg">
              <span>Tier:</span>{' '}
              {itApplication && itApplication.tier ? (
                <TierBadge
                  name={itApplication.tier.name}
                  color={itApplication.tier.color}
                  style={{
                    width: '80px',
                    padding: '0 8px',
                    textAlign: 'center',
                  }}
                />
              ) : (
                <span className="placeholder-text">Not Provided</span>
              )}
            </div>
          </div>
          {itApplication?.businessFunctions &&
            itApplication.businessFunctions.length > 0 && (
              <TableV3
                title="Business Functions"
                columns={businessFunctionsColumns}
                data={itApplication?.businessFunctions}
                loading={loading}
                useBorderedSection
                tableSize="small"
              />
            )}
          {itApplication?.qualitativeFactors &&
            itApplication.qualitativeFactors.length > 0 && (
              <TableV3
                title="Qualitative Factors"
                columns={qualitativeFactorsColumns}
                data={itApplication?.qualitativeFactors}
                loading={loading}
                useBorderedSection
                tableSize="small"
              />
            )}
          {itApplication?.processAreas &&
            itApplication.processAreas.length > 0 && (
              <TableV3
                title="Process Areas"
                columns={processAreasColumns}
                data={itApplication?.processAreas}
                loading={loading}
                useBorderedSection
                tableSize="small"
              />
            )}
        </div>
      </QueryErrorBoundary>
      <Footer backPath={backPath} />
    </>
  );
};

export default ApplicationWorkaround;
